import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { GroupPermissionsPanel } from "./GroupPermissionsPanel";
import { IndividualPermissionsPanel } from "./IndividualPermissionsPanel";
import { PermissionsAuditPanel } from "./PermissionsAuditPanel";

// PRD-001AE L2: Group Permissions | Individual Permissions | Audit Log.
//
// AE3 shipped Group, AE4 added Individual, AE5 completes the strip with Audit. The three tabs are
// the same question in three tenses: what a role may do, who holds that role, and what was changed.
//
// Runtime policy panels are deliberately NOT tabs here. They are settings, not permissions
// (AC-001AE.5), and they keep their own Admin sections.

export const PERMISSIONS_TABS = ["group", "individual", "audit"] as const;
export type PermissionsTab = (typeof PERMISSIONS_TABS)[number];

export const PERMISSIONS_TAB_LABELS: Readonly<Record<PermissionsTab, string>> = {
  group: "Group permissions",
  individual: "Individual permissions",
  audit: "Audit log",
};

export const PERMISSIONS_TAB_HINTS: Readonly<Record<PermissionsTab, string>> = {
  group: "What each role may do in this workspace.",
  individual: "Who holds which role.",
  audit: "What was changed, by whom, and when. Read only.",
};

export function PermissionsTabs({
  client,
  accountId,
  timeZone,
  canView = false,
  canManage = false,
  onPermissionsChanged,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  /** Display zone for audit timestamps. The editing tabs have no timestamps to render. */
  timeZone: string;
  canView?: boolean;
  canManage?: boolean;
  onPermissionsChanged?: () => void;
  pollMs?: number;
}) {
  const [tab, setTab] = useState<PermissionsTab>("group");

  if (!canView) {
    return null;
  }

  const shared = {
    client,
    accountId,
    canView: true,
    canManage,
    ...(onPermissionsChanged !== undefined ? { onPermissionsChanged } : {}),
    ...(pollMs !== undefined ? { pollMs } : {}),
  };

  return (
    <div className="permissions-tabs">
      <div className="permissions-tabs__strip" role="tablist" aria-label="Permissions">
        {PERMISSIONS_TABS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            id={`permissions-tab-${candidate}`}
            /* Only the selected panel is in the DOM, so only the selected tab may claim to control
               one. Pointing at an id that is not rendered gives a screen reader a dangling
               reference; AE4 shipped that for two tabs and a third would widen it. */
            {...(tab === candidate ? { "aria-controls": `permissions-panel-${candidate}` } : {})}
            aria-selected={tab === candidate}
            className={`permissions-tabs__tab ${tab === candidate ? "permissions-tabs__tab--active" : ""}`}
            onClick={() => setTab(candidate)}
          >
            {PERMISSIONS_TAB_LABELS[candidate]}
          </button>
        ))}
      </div>
      <p className="permissions-tabs__hint">{PERMISSIONS_TAB_HINTS[tab]}</p>

      <div
        role="tabpanel"
        id={`permissions-panel-${tab}`}
        aria-labelledby={`permissions-tab-${tab}`}
      >
        {/* Only the selected panel is mounted, so the tabs that are not on screen issue no read.
            That matters most for Audit, whose filtered scan is the heaviest read in this section
            and which deliberately does not take pollMs: it is a log, not a live rail. */}
        {tab === "group" ? <GroupPermissionsPanel {...shared} /> : null}
        {tab === "individual" ? <IndividualPermissionsPanel {...shared} /> : null}
        {tab === "audit" ? (
          <PermissionsAuditPanel client={client} accountId={accountId} timeZone={timeZone} canView />
        ) : null}
      </div>
    </div>
  );
}
